import { FileText, Pencil, Plus, Search, UserRound, Users, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  NOME_ESTADO_CIVIL,
  NOME_NATUREZA,
  type Natureza,
  type Pagina,
  type Pessoa,
} from '@social/shared';
import { AcoesLinha } from '../componentes/acoes-linha';
import { AlternadorVisao, useVisao } from '../componentes/alternador-visao';
import { CartaoKpi } from '../componentes/cartao-kpi';
import { PaginaShell } from '../componentes/pagina-shell';
import { SeletorOpcao, type OpcaoSeletor } from '../componentes/seletor-opcao';
import { Tabela, type Coluna, type Ordenacao } from '../componentes/tabela';
import { ACAO_PRIMARIA_LINK, Aviso, CONTROLE_BARRA, Entrada } from '../componentes/ui';
import { Spinner } from '../componentes/carregando';
import { comEspera } from '../lib/ocupado';
import { api } from '../lib/sessao';
import { CartaoPessoa } from './pessoa-cartao';
import {
  digitosDoDocumento,
  documentoDe,
  ehFisica,
  idadeDe,
  localDe,
  nomeDe,
  qualificacaoDe,
} from './pessoa-comum';

/*
  Consulta de sócios e administradores.

  Nasce do MOLDE (`PaginaShell`) e dos componentes que a consulta de sociedades
  já amadureceu — barra de uma linha, busca confirmada, paginação sempre
  visível, cards abaixo de `sm`. Nada aqui é decisão nova de layout: se
  parecer diferente da outra consulta, é defeito.

  ## As seis perguntas (skill `social-desenho`)
  1. **Qual pergunta a pessoa traz?** "Quem eu já cadastrei que pode ser sócio
     ou administrador — e essa pessoa já está aqui ou preciso cadastrar?"
  2. **Quantos registros na vida real?** Dezenas a poucas centenas: um
     escritório com 100 sociedades tem umas 250 pessoas. Paginação sim, KPI
     enxuto, filtro pesado não.
  3. **Resultado vs. aparência?** Busca e natureza mudam o resultado e ficam
     soltas; colunas e atualizar colapsam no menu de aparência.
  4. **Barra vs. gaveta?** Não há gaveta: com dois filtros de uso diário, uma
     gaveta ficaria vazia — e gaveta vazia é clique que não devolve nada.
  5. **375px?** A `Tabela` já troca para cards abaixo de `sm`.
  6. **O que promete e não existe?** Pessoa não tem tela de DETALHE — a API
     não tem `GET /pessoas/:id`. Tem edição (`PATCH`), e é para lá que o nome
     leva. Enquanto o `PATCH` não existia, o nome era texto puro de propósito:
     link que não leva a lugar nenhum é promessa falsa.
*/

const OPCOES_NATUREZA: readonly OpcaoSeletor<Natureza | ''>[] = [
  { valor: '', rotulo: 'Todas as naturezas', Icone: Users },
  {
    valor: 'FISICA',
    rotulo: NOME_NATUREZA.FISICA,
    descricao: 'Sócio, administrador, procurador ou testemunha',
    Icone: UserRound,
  },
  {
    valor: 'JURIDICA',
    rotulo: NOME_NATUREZA.JURIDICA,
    descricao: 'Sócia de outra sociedade — não tem estado civil nem idade',
    Icone: FileText,
  },
];

export function Pessoas(): JSX.Element {
  const navegar = useNavigate();
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [natureza, setNatureza] = useState<Natureza | ''>('');
  const [ordenacao, setOrdenacao] = useState<Ordenacao | null>(null);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(10);
  const [visao, setVisao] = useVisao('pessoas.consulta');

  const pessoasQuery = useQuery({
    queryKey: ['pessoas', 'consulta'] as const,
    queryFn: () => api.get<Pagina<Pessoa>>('/pessoas?tamanho=200'),
  });

  const consultando = pessoasQuery.isFetching;

  const aplicarBusca = () => {
    setBuscaAplicada(busca);
    setPaginaAtual(1);
    void comEspera(() => pessoasQuery.refetch(), 'Consultando…');
  };

  const todas = useMemo(() => pessoasQuery.data?.itens ?? [], [pessoasQuery.data]);

  // Um `filter` só, com o que não depende da LINHA normalizado aqui fora —
  // mesma forma da consulta de sociedades, pelo mesmo motivo.
  const visiveis = useMemo(() => {
    const termo = buscaAplicada.trim().toLowerCase();
    const digitos = buscaAplicada.replace(/\D/g, '');

    return todas.filter((p) => {
      if (natureza && p.natureza !== natureza) return false;
      if (!termo) return true;
      if (nomeDe(p).toLowerCase().includes(termo)) return true;
      // O documento é guardado só com dígitos: quem digita "123.456" precisa
      // achar do mesmo jeito que quem digita "123456".
      return digitos !== '' && digitosDoDocumento(p).includes(digitos);
    });
  }, [todas, buscaAplicada, natureza]);

  const ordenadas = useMemo(() => {
    if (!ordenacao) return visiveis;
    const sinal = ordenacao.direcao === 'asc' ? 1 : -1;
    return [...visiveis].sort((a, b) => {
      const va = valorDaColuna(a, ordenacao.chave);
      const vb = valorDaColuna(b, ordenacao.chave);
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * sinal;
      return String(va).localeCompare(String(vb), 'pt-BR') * sinal;
    });
  }, [visiveis, ordenacao]);

  const totalPaginas = Math.max(1, Math.ceil(ordenadas.length / tamanhoPagina));
  const paginaValida = Math.min(paginaAtual, totalPaginas);
  const daPagina = useMemo(
    () =>
      ordenadas.slice((paginaValida - 1) * tamanhoPagina, paginaValida * tamanhoPagina),
    [ordenadas, paginaValida, tamanhoPagina],
  );

  const kpis = useMemo(() => {
    const fisicas = todas.filter(ehFisica).length;
    return { total: todas.length, fisicas, juridicas: todas.length - fisicas };
  }, [todas]);

  const haDados = kpis.total > 0;
  const haRecorte = busca !== '' || buscaAplicada !== '' || natureza !== '';
  const buscaPendente = busca.trim() !== buscaAplicada.trim();

  const limparFiltros = () => {
    setBusca('');
    setBuscaAplicada('');
    setNatureza('');
    setPaginaAtual(1);
  };

  const colunas: Coluna<Pessoa>[] = [
    {
      chave: 'codigo',
      titulo: 'Código',
      ordenavel: true,
      celula: (p) => <span className="text-tinta-500 tabular-nums">{p.codigo}</span>,
    },
    {
      chave: 'nome',
      titulo: 'Nome',
      ordenavel: true,
      papel: 'titulo',
      /*
        Agora o nome É link.

        Ficou como texto puro enquanto a API só tinha `POST` e `GET`: um nome
        que parece clicável e não leva a lugar nenhum é promessa falsa. Com o
        `PATCH` existindo, o clique leva à correção — que é o que se quer
        fazer ao encontrar uma pessoa na lista.
      */
      celula: (p) => (
        <Link
          to={`/pessoas/${p.id}/editar`}
          className="text-tinta-900 font-medium hover:underline"
        >
          {nomeDe(p)}
        </Link>
      ),
    },
    {
      chave: 'natureza',
      titulo: 'Natureza',
      ordenavel: true,
      celula: (p) => (ehFisica(p) ? 'PF' : 'PJ'),
    },
    {
      chave: 'documento',
      titulo: 'CPF / CNPJ',
      celula: (p) => <span className="tabular-nums">{documentoDe(p)}</span>,
    },
    {
      chave: 'qualificacao',
      titulo: 'Qualificação',
      ocultarNoCelular: true,
      celula: (p) => qualificacaoDe(p),
    },
    {
      chave: 'idade',
      titulo: 'Idade',
      ordenavel: true,
      ocultarNoCelular: true,
      celula: (p) => {
        const i = idadeDe(p);
        return i === undefined ? '—' : <span className="tabular-nums">{i}</span>;
      },
    },
    {
      chave: 'local',
      titulo: 'Município/UF',
      ordenavel: true,
      celula: (p) => localDe(p),
    },
  ];

  return (
    <PaginaShell
      titulo="Sócios e administradores"
      subtitulo="As pessoas que assinam os atos das suas sociedades"
      /*
        `Link` com a aparência do botão primário, e não `<Botao>` envolvendo um
        link: o `Botao` renderiza `<button>`, e `<button>` dentro de `<a>` (ou o
        contrário) é HTML inválido — o teclado passa a ver dois controles onde a
        pessoa vê um. Aqui o elemento é UM link, que é o que ele de fato faz:
        navega.
      */
      acoes={
        <Link to="/pessoas/nova" className={ACAO_PRIMARIA_LINK}>
          <Plus className="size-4" aria-hidden />
          Nova pessoa
        </Link>
      }
      filtros={
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-center">
          <div className="col-span-full flex items-center gap-2 lg:min-w-72 lg:flex-1">
            <div className="relative min-w-0 flex-1">
              <Search
                className="text-tinta-500 pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
                aria-hidden
              />
              <Entrada
                aria-label="Buscar pessoa"
                placeholder="Buscar por nome, CPF ou CNPJ…"
                className="pr-9 pl-9"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    aplicarBusca();
                  }
                }}
              />
              {busca && (
                <button
                  type="button"
                  onClick={() => {
                    setBusca('');
                    setBuscaAplicada('');
                  }}
                  aria-label="Limpar a busca"
                  title="Limpar a busca"
                  className="text-tinta-500 hover:text-tinta-900 absolute top-1/2 right-2 inline-flex size-7 -translate-y-1/2 items-center justify-center rounded-md transition-colors"
                >
                  <X className="size-4" aria-hidden />
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={aplicarBusca}
              disabled={consultando}
              aria-label="Consultar"
              aria-busy={consultando}
              title={consultando ? 'Consultando…' : 'Consultar (ou tecle Enter)'}
              className={`${CONTROLE_BARRA} w-11 shrink-0 justify-center ${
                buscaPendente && !consultando ? 'border-marca-600 text-marca-600' : ''
              }`}
            >
              {consultando ? (
                <Spinner tamanho="sm" />
              ) : (
                <Search className="size-4" aria-hidden />
              )}
            </button>
          </div>

          <div className="lg:contents [&_button]:w-full lg:[&_button]:w-auto">
            <SeletorOpcao<Natureza | ''>
              rotulo="Filtrar por natureza"
              IconeGatilho={Users}
              larguraPainel="w-80"
              valor={natureza}
              aoEscolher={(n) => {
                setNatureza(n);
                setPaginaAtual(1);
              }}
              opcoes={OPCOES_NATUREZA}
            />
          </div>

          <div className="col-span-full flex items-center justify-end gap-2 lg:contents">
            {haRecorte && (
              <button
                type="button"
                onClick={limparFiltros}
                title="Limpar busca e natureza"
                className={`${CONTROLE_BARRA} px-3`}
              >
                <X className="size-4" aria-hidden />
                Limpar
              </button>
            )}
            <AlternadorVisao visao={visao} aoTrocar={setVisao} />
          </div>
        </div>
      }
      indicadores={
        haDados ? (
          <>
            <CartaoKpi rotulo="Pessoas" valor={String(kpis.total)} icone={Users} />
            <CartaoKpi
              rotulo="Pessoas físicas"
              valor={String(kpis.fisicas)}
              icone={UserRound}
            />
            <CartaoKpi
              rotulo="Pessoas jurídicas"
              valor={String(kpis.juridicas)}
              icone={FileText}
            />
          </>
        ) : undefined
      }
    >
      {pessoasQuery.isError && <Aviso>Não foi possível carregar as pessoas.</Aviso>}

      <Tabela
        colunas={colunas}
        visao={visao}
        /*
          Uma ação só, e a mesma do clique no nome. Não é redundância: no
          cartão (celular) não há coluna de nome para clicar, e é por aqui que
          se chega à correção.
        */
        acoes={(p) => (
          <AcoesLinha
            acoes={[
              {
                rotulo: 'Editar',
                icone: Pencil,
                aoEscolher: () => navegar(`/pessoas/${p.id}/editar`),
              },
            ]}
          />
        )}
        cartao={(p, acoesDaLinha) => <CartaoPessoa pessoa={p} acoes={acoesDaLinha} />}
        pagina={{
          itens: daPagina,
          total: ordenadas.length,
          pagina: paginaValida,
          totalPaginas,
          tamanho: tamanhoPagina,
        }}
        aoTrocarPagina={setPaginaAtual}
        aoTrocarTamanho={(n) => {
          setTamanhoPagina(n);
          setPaginaAtual(1);
        }}
        carregando={pessoasQuery.isPending}
        chaveDe={(p) => p.id}
        ordenacao={ordenacao}
        aoOrdenar={(o) => {
          setOrdenacao(o);
          setPaginaAtual(1);
        }}
        vazio={
          haRecorte
            ? {
                titulo: 'Nenhuma pessoa com esse recorte.',
                descricao: 'Ajuste a busca ou a natureza.',
              }
            : {
                titulo: 'Nenhuma pessoa cadastrada ainda.',
                descricao: 'Cadastre quem vai assinar os atos das suas sociedades.',
              }
        }
      />
    </PaginaShell>
  );
}

/**
 * O valor pelo qual cada coluna ordena.
 *
 * Separado do desenho da coluna porque a célula devolve JSX (que não se
 * compara) e porque `nome` e `documento` moram em campos diferentes conforme
 * a natureza — ordenar pelo campo cru deixaria as jurídicas todas juntas no
 * fim, o que parece defeito.
 */
function valorDaColuna(p: Pessoa, chave: string): string | number {
  switch (chave) {
    case 'codigo':
      return p.codigo;
    case 'nome':
      return nomeDe(p);
    case 'natureza':
      return p.natureza;
    case 'idade':
      // Sem nascimento vai para o fim, não para o começo: `-1` faria toda
      // pessoa jurídica encabeçar a ordem crescente por idade.
      return idadeDe(p) ?? Number.MAX_SAFE_INTEGER;
    case 'local':
      return localDe(p);
    default:
      return nomeDe(p);
  }
}

/** Rótulo do estado civil, para quem importar daqui em vez do shared. */
export { NOME_ESTADO_CIVIL };
